/**
 * Christmas card ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_christmas_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const christmasCardIdeasPack: ToilePack = {
    slug: 'christmas-card-ideas',
    category: 'season',
    name: 'Christmas card ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'popup', label: 'Pop-up', subject: 'a handmade pop-up tree card'},
        {key: 'watercolor', label: 'Watercolor', subject: 'a watercolor card with a snowy scene'},
        {key: 'buttons', label: 'Buttons', subject: 'a card with a tree made of buttons'},
    ],
    styles: [
        {key: 'flatlay', label: 'Flat lay', style: 'an overhead flat lay on a wooden craft table with scissors, paper scraps and twine around it, soft window daylight from camera-left, an even balance with soft shadows', palette: 'kraft brown, red, green and white', framing: 'the card centered and seen from directly above'},
        {key: 'shelf', label: 'On display', style: 'the card standing open on a shelf beside a sprig of pine and a candle, warm evening light from camera-right, a cosy balance of warm highlights and soft shadow', palette: "warm cream and gold with the card's own colors", framing: 'three-quarter view at card height, the room softly blurred'},
        {key: 'macro', label: 'Paper detail', style: 'a close detail of the paper texture, folds and layered edges with shallow depth of field, soft daylight from above, a bright balance', palette: "the card's colors on textured cotton paper", framing: 'tight crop on the card front'},
    ],
    directions: ['kids cards', 'pressed flowers', 'gold foil'],
    directionHint: 'try: kids cards',
    seo: {
        title: 'Christmas Card Ideas: Pop-up, Watercolor, Button Card',
        metaDescription: 'Nine photos of handmade Christmas cards: a pop-up tree, a watercolor snow scene and a button tree, flat, on display and close up.',
        h1: 'Christmas card ideas: pop-up, watercolor, button card',
        tagline: 'Three handmade cards, each shown laid flat, standing on a shelf and as a close look at the paper.',
        intro: 'Handmade Christmas cards in three techniques: a pop-up tree that stands up when opened, a watercolor card with a snowy scene and a card with a tree made of buttons. Each is shown flat from above, standing open on a shelf and up close. Use them to pick a card to make. Type a twist, such as kids cards or gold foil, and the cards are asked to follow it.',
        useCases: [
            'a card to copy for a class or a family',
            'a craft table project for a rainy weekend',
            'a set of ideas for a holiday card swap',
            'a card to make for a neighbor',
        ],
        faq: [
            {q: 'What are the three techniques?', a: 'A handmade pop-up tree, a watercolor card with a snowy scene, and a card with a tree made of buttons.'},
            {q: 'Is there text on the cards?', a: 'Yes. Merry Christmas is handwritten small on each card, and no other words appear.'},
            {q: 'What do the three styles show?', a: 'Flat lay shows the card from above with scissors, paper scraps and twine around it. On display shows the card standing open beside a sprig of pine and a candle. Paper detail is a close look at the texture and folded edges.'},
            {q: 'Can I ask for a different card?', a: 'Yes. Type a twist, such as pressed flowers, and the page asks for a card that follows it.'},
        ],
    },
}
